import React, { useMemo } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Globe, Leaf, Film, Trophy, Sparkles, Shuffle, Crown, UtensilsCrossed, Gamepad2, Shirt, Music2, Clapperboard, Tv } from 'lucide-react';
import { CATEGORIES } from '../mock/gameData';

// Twinkling stars background component
const TwinklingStars = () => {
  const stars = useMemo(() => {
    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      duration: `${2 + Math.random() * 4}s`,
      delay: `${Math.random() * 5}s`,
      color: ['white', 'pink', 'cyan'][Math.floor(Math.random() * 3)],
      size: Math.random() > 0.7 ? 4 : 2,
    }));
  }, []);

  return (
    <div className="stars-container">
      {stars.map((star) => (
        <div
          key={star.id}
          className={`star ${star.color}`}
          style={{
            left: star.left,
            top: star.top,
            width: `${star.size}px`,
            height: `${star.size}px`,
            '--duration': star.duration,
            '--delay': star.delay,
          }}
        />
      ))}
    </div>
  );
};

const CategorySelector = ({ onCategorySelect, onBack }) => {
  
  const categories = [
    {
      id: CATEGORIES.MUSIC,
      name: 'Music',
      description: 'Artists, bands, and hit songs.',
      icon: <Music2 className="w-12 h-12 text-purple-400" />,
      color: "bg-purple-900/40 border-purple-500 hover:bg-purple-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]"
    },
    {
      id: CATEGORIES.MOVIES,
      name: 'Movies',
      description: 'Films, actors, and box office hits.',
      icon: <Clapperboard className="w-12 h-12 text-red-400" />,
      color: "bg-red-900/40 border-red-500 hover:bg-red-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(239,68,68,0.4)]"
    },
    {
      id: CATEGORIES.ENTERTAINMENT,
      name: 'Entertainment',
      description: 'Books, celebrities, TV shows, and Marvel.',
      icon: <Tv className="w-12 h-12 text-pink-400" />,
      color: "bg-pink-900/40 border-pink-500 hover:bg-pink-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(236,72,153,0.4)]"
    },
    {
      id: CATEGORIES.SPORTS,
      name: 'Sports',
      description: 'Football, soccer, baseball, and more.',
      icon: <Trophy className="w-12 h-12 text-orange-400" />,
      color: "bg-orange-900/40 border-orange-500 hover:bg-orange-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]"
    },
    {
      id: CATEGORIES.THE_GLOBE,
      name: 'The Globe',
      description: 'Cities, countries, rivers, and world facts.',
      icon: <Globe className="w-12 h-12 text-blue-400" />,
      color: "bg-blue-900/40 border-blue-500 hover:bg-blue-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(59,130,246,0.4)]"
    },
    {
      id: CATEGORIES.LIVING_THINGS,
      name: 'Living Things',
      description: 'Animals, plants, and people names.',
      icon: <Leaf className="w-12 h-12 text-green-400" />,
      color: "bg-green-900/40 border-green-500 hover:bg-green-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(34,197,94,0.4)]"
    },
    {
      id: CATEGORIES.FOOD_AND_DRINK,
      name: 'Food & Drink',
      description: 'Fast food, snacks, drinks, and tasty brands.',
      icon: <UtensilsCrossed className="w-12 h-12 text-yellow-400" />,
      color: "bg-yellow-900/40 border-yellow-500 hover:bg-yellow-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(234,179,8,0.4)]"
    },
    {
      id: CATEGORIES.GAMING,
      name: 'Gaming',
      description: 'Video games, consoles, and digital legends.',
      icon: <Gamepad2 className="w-12 h-12 text-red-400" />,
      color: "bg-red-900/40 border-red-500 hover:bg-red-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(248,113,113,0.4)]"
    },
    {
      id: CATEGORIES.STYLE,
      name: 'Style',
      description: 'Sneakers, fashion, and collectible drip.',
      icon: <Shirt className="w-12 h-12 text-rose-400" />,
      color: "bg-rose-900/40 border-rose-500 hover:bg-rose-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(251,113,133,0.4)]"
    },
    {
      id: CATEGORIES.EPHEMERA,
      name: 'The Abyss',
      description: 'Descend into chaos, nonsense, and the unknown.',
      icon: <Sparkles className="w-12 h-12 text-violet-400" />,
      color: "bg-violet-900/40 border-violet-500 hover:bg-violet-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(139,92,246,0.4)]"
    },
    {
      id: CATEGORIES.RANDOM,
      name: 'Random',
      description: 'Spin the wheel! We pick a deck for you.',
      icon: <Shuffle className="w-12 h-12 text-cyan-400" />,
      color: "bg-cyan-900/40 border-cyan-500 hover:bg-cyan-800/50",
      glow: "hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
    }
  ];

  return (
    <div className="min-h-screen synthwave-bg-dark relative overflow-hidden p-4 flex items-center justify-center scan-lines">
      {/* Twinkling Stars */}
      <TwinklingStars />
      
      {/* Synthwave Sun - positioned left side */}
      <div className="synthwave-sun" style={{ right: 'auto', left: '5%', bottom: '5%', opacity: 0.5 }}></div>
      
      <div className="max-w-4xl w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            onClick={onBack}
            size="sm"
            variant="outline"
            className="neon-border-cyan bg-transparent text-cyan-300 hover:bg-cyan-500/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-2 tracking-wider">
            <span className="neon-text-dual" style={{ fontFamily: "'Outrun future', sans-serif" }}>
              <Crown className="w-8 h-8 inline-block mr-2 text-yellow-400" style={{ filter: 'drop-shadow(0 0 10px #ffd93d)' }} />
              Select Category
            </span>
          </h1>
          <p className="neon-text-cyan text-lg tracking-wide">Choose your battlefield</p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat) => (
            <Card 
              key={cat.id}
              className={`${cat.color} ${cat.glow} backdrop-blur-sm border-2 transition-all duration-300 transform hover:scale-105 cursor-pointer`}
              onClick={() => onCategorySelect(cat.id)}
            >
              <div className="p-6 flex flex-col items-center text-center">
                <div className="mb-3" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }}>
                  {cat.icon}
                </div>
                <h2 className="text-xl font-bold text-white mb-2 tracking-wide">{cat.name}</h2>
                <p className="text-gray-300 text-sm">{cat.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategorySelector;
